/**
 * The factory store, run as a store (owner, 21 Oct 2026: "the store workers
 * are saying they did the count it was deleted next day and when store item
 * are getting out of stock the count says the same it is all very basic").
 *
 * What the live ledger showed: no count was ever lost. The one shared sign-in
 * typed about ninety issues every late morning, so yesterday's usage, typed
 * after a fresh count, took the shelf down a second time; and parts used but
 * never typed left figures standing while the shelf ran empty. Two COUNTs in
 * three weeks.
 *
 * So, as the owner chose:
 *   - each model carries a parts list, drafted from the ledger and confirmed
 *     by the store ("I draft it, store confirms");
 *   - the store picks a model's parts for the line for N vehicles in one
 *     go, scanning their boxes (since 21 Oct 2026, factory-boxes.ts — at
 *     first a kit took them off by itself, and a PDI pass with no kit behind
 *     it backflushed them); a PDI pass now only writes the vehicle down;
 *   - a count is a snapshot of the shelf at the moment it was counted, and
 *     anything dated before it never moves the figure again;
 *   - counts are blind; a big gap is counted once more, and if it holds,
 *     the owner's desks or Amit decide ("You and Amit both").
 */
import { isHeadAdminDesk } from './domain.js';
import { familyForStockModel } from './production.js';
import type { FactoryLineCheck } from './factory-boxes.js';

// ---------------------------------------------------------------------------
// Parts lists
// ---------------------------------------------------------------------------

/**
 * The chassis the factory builds today, busiest first — each carries one parts
 * list. A list follows the chassis, because a PDI pass names the chassis: the
 * Jumbo has gone out on the Pro chassis since September (production notes,
 * 13 Oct 2026), so its parts go out — and come off at PDI — as the Pro's.
 */
export const BOM_FAMILIES = ['EMAX', 'PRO', 'SUPER', 'CARGO', 'MINI'] as const;
export type BomFamily = (typeof BOM_FAMILIES)[number];
export const BOM_FAMILY_LABELS: Record<BomFamily, string> = { EMAX: 'E-Max', PRO: 'Pro & loaders', SUPER: 'Super', CARGO: 'Old Cargo', MINI: 'Mini Loader' };
/** One line under each card and column: which vehicles a list is for. */
export const BOM_FAMILY_HINTS: Record<BomFamily, string> = {
  EMAX: 'Every E-Max, ECO and DLX.',
  PRO: 'The Pro chassis — the Pro, and the cargo bodies built on it: Jumbo, Dust Cart.',
  SUPER: 'Every Super, ECO and DLX.',
  CARGO: 'The old Cargo chassis still in hand, as it passes PDI.',
  MINI: 'Every Mini Loader — the Mini Cargo body, "MINI" in the variant.',
};

/**
 * The Pro and the old Cargo chassis are one loader line (production notes, 13
 * Oct 2026: "the CC Jumbo, CC Mini Cargo, CC Dust Cart and the customised
 * cargo bodies are built on the CITY CAB PRO chassis now; the old CITY CAB
 * CARGO chassis still in hand fills them too"). A kit sent for one may be
 * used up by a vehicle passing on the other.
 */
export const LOADER_SIBLING: Partial<Record<BomFamily, BomFamily>> = { PRO: 'CARGO', CARGO: 'PRO' };

export function isBomFamily(value: string | null | undefined): value is BomFamily {
  return !!value && (BOM_FAMILIES as readonly string[]).includes(value);
}

/** The list a vehicle passing PDI draws on: its chassis as the yard files it. */
export function bomFamilyForVehicle(model: string | null | undefined, variant?: string | null): BomFamily | null {
  // The Mini Loader has a list of its own (factory, 22 Oct 2026: "MINI LOADER ADD HOGA"): the books
  // write it "CITYCAB CARGO" with the variant "MINI ECO Li", so the variant says which it is.
  if (/MINI/.test(`${model ?? ''} ${variant ?? ''}`.toUpperCase())) return 'MINI';
  const family = familyForStockModel(model);
  return isBomFamily(family) ? family : null;
}

/**
 * The list an order line will be built on. The production plan names the body
 * ordered ("CC Jumbo", "CC Dust Cart"); the parts follow the chassis it is
 * built on — every cargo body on the Pro, now.
 */
export function bomFamilyForPlanModel(model: string | null | undefined): BomFamily | null {
  const squashed = (model ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  // The Mini Loader on its own list (22 Oct 2026); every other cargo body is built on the Pro chassis.
  if (/MINICARGO|MINILOADER/.test(squashed)) return 'MINI';
  if (/JUMBO|DUSTCART|CARGO|LOADER/.test(squashed)) return 'PRO';
  return bomFamilyForVehicle(model);
}

/** How many vehicles one kit may be for — a day's line, with room. */
export const KIT_MAX_VEHICLES = 200;
/** Pieces of one part per vehicle, at most. */
export const BOM_MAX_PER_VEHICLE = 500;

export type FactoryBomStatusKey = 'DRAFT' | 'CONFIRMED';

export interface FactoryBomLineView {
  itemId: string;
  name: string;
  unit: string;
  category: string | null;
  perVehicle: number;
  /**
   * On the list, its quantity typed each time the list is picked (factory, 22
   * Oct 2026: "company name and watt confirm nahi hota", "kuch (0) qty manual
   * type krna hoga list k sath") — the motor whose make and watt vary, the wire
   * by the metre. Its perVehicle is nought.
   */
  typed: boolean;
  /** What the ledger showed per vehicle when drafted, or null. */
  suggested: number | null;
  /** On the shelf now. */
  quantity: number;
}

export interface FactoryBomView {
  family: BomFamily;
  label: string;
  status: FactoryBomStatusKey;
  /** Vehicles whose parts are on the line and that have not passed PDI. */
  onLine: number;
  confirmedAt: string | null;
  confirmedBy: string | null;
  liveFrom: string | null;
  lines: FactoryBomLineView[];
  /** Vehicles this list could still be built for from the shelf, as it stands — the scarcest part decides. */
  buildable: number | null;
  /** The part that decides it. */
  limitedBy: string | null;
  updatedAt: string | null;
}

/** One cell of the parts matrix, as the store saves it: nought takes the part off the list. */
export interface FactoryBomSaveLine {
  itemId: string;
  perVehicle: number;
  /** On the list with its quantity typed each time it is picked; perVehicle is then nought. */
  typed?: boolean;
}

// ---------------------------------------------------------------------------
// Sending parts to the line
// ---------------------------------------------------------------------------

export interface FactoryPickLine {
  itemId: string;
  name: string;
  unit: string;
  category: string | null;
  perVehicle: number;
  /** perVehicle × vehicles. */
  needed: number;
  onShelf: number;
  /** How many the shelf is short by, or nought. */
  short: number;
  /** Its quantity is typed when picking — needed is nought, nothing is short. */
  typed: boolean;
  /** The boxes to scan for it, oldest first, until `needed` is covered or they run out. */
  boxes: Array<{ code: string; boxNo: number; boxOf: number; remaining: number; pack: string | null }>;
  /** Pieces on the shelf in no box — typed out, not scanned. */
  loose: number;
}

export interface FactoryPickList {
  family: BomFamily;
  label: string;
  vehicles: number;
  status: FactoryBomStatusKey;
  onLine: number;
  lines: FactoryPickLine[];
  /** Parts the shelf cannot cover. */
  shortLines: number;
  /** Vehicles the shelf covers in full. */
  buildable: number;
}

export interface FactoryKitInput {
  family: BomFamily;
  vehicles: number;
  /** Who takes it to the line. */
  party: string;
  note?: string | null;
  /**
   * Changes the store made on the pick list: another part in place of one
   * on the list (a Tsuyo motor for an Ecotron), or a different quantity —
   * each part once, with the whole quantity it goes out with. Nought leaves
   * the part out of this kit.
   */
  lines?: Array<{ itemId: string; quantity: number }>;
}

export interface FactoryKitRow {
  id: string;
  family: BomFamily;
  label: string;
  vehicles: number;
  pieces: number;
  lines: number;
  party: string | null;
  note: string | null;
  by: string | null;
  at: string;
}

/** The line, model by model: kits out, vehicles through PDI, and what stands between. */
export interface FactoryLineState {
  families: Array<{
    family: BomFamily;
    label: string;
    status: FactoryBomStatusKey | null;
    lines: number;
    onLine: number;
    /** Today, IST: vehicles' parts picked for the line, and vehicles through PDI. */
    kittedToday: number;
    passedToday: number;
    buildable: number | null;
    limitedBy: string | null;
  }>;
  recentKits: FactoryKitRow[];
  /** Parts out to production against what the vehicles through PDI needed; null with no confirmed list. */
  check: FactoryLineCheck | null;
}

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

/**
 * How far a count may be from the app's figure and still be taken as it is:
 * two pieces, or one in twenty, whichever is more. Beyond it, the counter is
 * asked once more; beyond it again, the owner's desks or Amit decide.
 */
export function countTolerance(expected: number): number {
  return Math.max(2, Math.ceil(Math.abs(expected) * 0.05));
}

export function countGapIsSmall(expected: number, counted: number): boolean {
  return Math.abs(counted - expected) <= countTolerance(expected);
}

/**
 * Who decides a count the shelf and the app disagree on (owner, 21 Oct 2026:
 * "You and Amit both") — the owner's head-admin desks, and the factory's PDI
 * hand, the only one of whom is Amit.
 */
export function approvesFactory(user: { email: string; roleKey: string } | null | undefined): boolean {
  if (!user) return false;
  return isHeadAdminDesk(user) || user.roleKey === 'FACTORY_PDI';
}

/** Fast movers are counted every week, the middle every fortnight, the rest every month. */
export type CountClass = 'A' | 'B' | 'C';
export const COUNT_EVERY_DAYS: Record<CountClass, number> = { A: 7, B: 14, C: 30 };
/** Today's count is kept to what one person counts in half an hour. */
export const TODAYS_COUNT_SIZE = 25;

export type FactoryCountLineStatusKey = 'WAITING' | 'ACCEPTED' | 'RECOUNT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUPERSEDED' | 'SKIPPED';

export const COUNT_LINE_LABELS: Record<FactoryCountLineStatusKey, string> = {
  WAITING: 'To count',
  ACCEPTED: 'Set',
  RECOUNT: 'Count again',
  PENDING: 'Waiting for a decision',
  APPROVED: 'Approved',
  REJECTED: 'Not taken',
  SUPERSEDED: 'A newer count was set',
  SKIPPED: 'Not reached',
};

export type FactoryCountScope = 'TODAY' | 'CATEGORY' | 'ALL' | 'ITEMS';

export interface FactoryCountStart {
  scope: FactoryCountScope;
  category?: string | null;
  itemIds?: string[];
}

/** One line as the counter sees it — never the app's figure. */
export interface FactoryCountLineView {
  id: string;
  itemId: string;
  name: string;
  unit: string;
  category: string | null;
  code: string | null;
  status: FactoryCountLineStatusKey;
  counted: number | null;
  countedAt: string | null;
  countedBy: string | null;
}

export interface FactoryCountView {
  id: string;
  title: string;
  status: 'OPEN' | 'CLOSED';
  startedAt: string;
  by: string | null;
  lines: FactoryCountLineView[];
  progress: { total: number; counted: number; recount: number; pending: number };
}

/** What a counted line came to. */
export interface FactoryCountOutcome {
  line: FactoryCountLineView;
  /** ACCEPTED: the figure is set. RECOUNT: count it once more. PENDING: it waits for a decision. */
  outcome: 'ACCEPTED' | 'RECOUNT' | 'PENDING';
}

/** A line waiting for a decision — the approver sees both figures. */
export interface FactoryApprovalRow {
  lineId: string;
  countTitle: string;
  item: { id: string; name: string; unit: string; category: string | null };
  counted: number;
  /** The app's figure when it was counted. */
  expected: number;
  firstCount: number | null;
  firstExpected: number | null;
  /** counted − expected. */
  gap: number;
  countedAt: string;
  countedBy: string | null;
  /** What the counter wrote beside the figure, if anything. */
  note: string | null;
  /** Usage over the last fortnight, a day — to judge a gap by. */
  perDay: number;
}

// ---------------------------------------------------------------------------
// Stock health
// ---------------------------------------------------------------------------

/** Usage is read over the last fortnight. */
export const USAGE_WINDOW_DAYS = 14;

/**
 * OUT: nothing on the shelf. SHORT: less than the orders waiting to be built
 * need, or under three days of use left. LOW: at or under its low mark, or
 * under a week. OK: a week or more. IDLE: not used in a fortnight.
 */
export type StockHealthKey = 'OUT' | 'SHORT' | 'LOW' | 'OK' | 'IDLE';

export const STOCK_HEALTH_LABELS: Record<StockHealthKey, string> = {
  OUT: 'Finished',
  SHORT: 'Short',
  LOW: 'Low',
  OK: 'Enough',
  IDLE: 'Not moving',
};

export function stockHealthOf(input: { quantity: number; lowAt: number | null; perDay: number; planNeed: number }): StockHealthKey {
  const { quantity, lowAt, perDay, planNeed } = input;
  if (quantity <= 0) return 'OUT';
  const cover = perDay > 0 ? quantity / perDay : Infinity;
  if ((planNeed > 0 && quantity < planNeed) || cover < 3) return 'SHORT';
  if ((lowAt !== null && quantity <= lowAt) || cover < 7) return 'LOW';
  if (perDay <= 0) return 'IDLE';
  return 'OK';
}

/** A low mark that holds ten days of use — what the store is offered to set. */
export function suggestedLowAt(perDay: number): number | null {
  return perDay > 0 ? Math.ceil(perDay * 10) : null;
}

export interface FactoryHealthRow {
  itemId: string;
  name: string;
  unit: string;
  category: string | null;
  quantity: number;
  lowAt: number | null;
  /** Pieces a day over the last fortnight. */
  perDay: number;
  /** Days the shelf lasts at that pace, or null when it is not used. */
  coverDays: number | null;
  /** Pieces the orders waiting to be built need, by the parts lists. */
  planNeed: number;
  health: StockHealthKey;
  countClass: CountClass;
  countedAt: string | null;
  /** Due for a count today. */
  countDue: boolean;
  /** The models whose parts list carries it. */
  families: BomFamily[];
}

/** Sent beside the page of rows (`Paginated<FactoryHealthRow>`). */
export interface FactoryHealth {
  totals: Record<StockHealthKey, number>;
  /** Vehicles the orders still need built, by model — what planNeed is read from. */
  planVehicles: Partial<Record<BomFamily, number>>;
  /** Items counted in the last thirty days, of all. */
  countedLast30: number;
  items: number;
  /** Lines waiting for a decision. */
  waitingDecision: number;
  dueToday: number;
  asOf: string;
}
